import React, { useState } from "react";
import { X, Menu, Search } from "lucide-react";
import { Link } from "react-router-dom";
import "./header.css";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    document.body.style.overflow = !isMenuOpen ? "hidden" : "unset";
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <div className="flex-shrink-0 w-64 md:w-96 lg:w-[500px]">
            <Link to="/" className="flex items-center">
              <img
                src="../assets/images/logo_03.png"
                alt="MRG Logo"
                className="h-auto w-full object-contain max-h-16 md:max-h-20"
                style={{
                  aspectRatio: "1378/190",
                }}
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            <Link
              to="/"
              className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium"
            >
              Home
            </Link>
            <Link
              to="/about-us"
              className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium"
            >
              About Us
            </Link>
            <Link
              to="/gallery"
              className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium"
            >
              Gallery
            </Link>
            <Link
              to="/contactus"
              className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium"
            >
              Contacts
            </Link>
          </nav>

          {/* Rest of the component remains the same */}
          {/* Icons */}
          <div className="flex items-center space-x-4">
            <button
              onClick={toggleSearch}
              className="p-2 hover:bg-gray-100 rounded-full"
              aria-label="Search"
            >
              <Search className="h-6 w-6" />
            </button>
            <button
              onClick={toggleMenu}
              className="p-2 hover:bg-gray-100 rounded-full"
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div
          className={`overflow-hidden transition-all duration-300 ${
            isSearchOpen ? "h-16" : "h-0"
          }`}
        >
          <div className="py-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200"
              />
              <Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Overlay */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={toggleMenu}
        />
      )}

      {/* Mobile Menu Panel */}
      <div
        className={`fixed top-0 right-0 w-full md:w-96 h-full bg-white transform transition-transform duration-300 ease-in-out z-50 ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="h-full overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-8">
              {/* Mobile Logo */}
              <div className="w-40">
                <img
                  src="../assets/images/logo_03.png"
                  alt="MRG Logo"
                  className="h-auto w-full object-contain"
                  style={{
                    aspectRatio: "1378/190",
                  }}
                />
              </div>
              <button
                onClick={toggleMenu}
                className="p-2 hover:bg-gray-100 rounded-full"
                aria-label="Close menu"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Rest of the mobile menu content remains the same */}
            <nav className="space-y-6">
              <Link
                to="/"
                className="block text-2xl font-medium hover:text-gray-600"
                onClick={toggleMenu}
              >
                HOME
              </Link>
              <Link
                to="/about-us"
                className="block text-2xl font-medium hover:text-gray-600"
                onClick={toggleMenu}
              >
                About Us
              </Link>
              <Link
                to="/gallery"
                className="block text-2xl font-medium hover:text-gray-600"
                onClick={toggleMenu}
              >
                Gallery
              </Link>
              <Link
                to="/contactus"
                className="block text-2xl font-medium hover:text-gray-600"
                onClick={toggleMenu}
              >
                Contact Us
              </Link>
            </nav>

            {/* Footer Links */}
            <div className="mt-12 space-y-6">
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-400 uppercase">
                  Customer Service
                </h3>
                <a
                  href="tel:+917339967115"
                  className="block text-sm text-gray-600 hover:text-gray-900"
                >
                  +91-7339967115
                </a>
                <a
                  href="tel:+917023894408"
                  className="block text-sm text-gray-600 hover:text-gray-900"
                >
                  +91-7023894408
                </a>
                <a
                  href="mailto:mrgmultisportsclub@gmail.com"
                  className="block text-sm text-gray-600 hover:text-gray-900"
                >
                  mrgmultisportsclub@gmail.com
                </a>
              </div>
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-400 uppercase">
                  Social Media
                </h3>
                <a
                  href="https://www.facebook.com/godara146?mibextid=2JQ9oc"
                  className="block text-sm text-gray-600 hover:text-gray-900"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Facebook
                </a>
                <a
                  href="https://wa.me/+917023894408"
                  className="block text-sm text-gray-600 hover:text-gray-900"
                >
                  Whatsapp
                </a>
                <a
                  href="https://www.instagram.com/mrg_multisportsclub"
                  className="block text-sm text-gray-600 hover:text-gray-900"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Instagram
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
